import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Eye, EyeOff, RotateCw, Move, ZoomIn, ZoomOut, Download, RefreshCw } from 'lucide-react';
import * as THREE from 'three';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageOverlay = ({ sessionId, modelData, onClose }) => {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const pointCloudRef = useRef(null);
  
  const [availableImages, setAvailableImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [overlayOpacity, setOverlayOpacity] = useState(0.7);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [modelScale, setModelScale] = useState(1.0);
  const [modelPosition, setModelPosition] = useState({ x: 0, y: 0 });
  const [modelRotation, setModelRotation] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showWireframe, setShowWireframe] = useState(true);
  const [showDepthPoints, setShowDepthPoints] = useState(true);
  const [showColorScale, setShowColorScale] = useState(true);
  const [showAnnotations, setShowAnnotations] = useState(true);

  useEffect(() => {
    loadAvailableImages();
    initializeRenderer();
    
    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, [sessionId]);

  useEffect(() => {
    if (selectedImage) {
      loadImageData();
    }
  }, [selectedImage]);

  useEffect(() => {
    if (selectedImageData && modelData) {
      renderOverlay();
    }
  }, [selectedImageData, modelData, overlayOpacity, overlayVisible, modelScale, modelPosition, modelRotation, showWireframe, showDepthPoints, showColorScale, showAnnotations]);

  const loadAvailableImages = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/images`);
      setAvailableImages(response.data.images);
      if (response.data.images.length > 0) {
        setSelectedImage(response.data.images[0].filename);
      }
    } catch (error) {
      console.error('Failed to load images:', error);
    }
  };

  const loadImageData = async () => {
    if (!selectedImage) return;
    
    setLoading(true);
    try {
      const imageUrl = `${API}/sessions/${sessionId}/images/${selectedImage}`;
      console.log('Loading image from:', imageUrl);
      
      // Load image with proper error handling
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log('Image loaded successfully:', img.width, 'x', img.height);
        setSelectedImageData({
          image: img,
          width: img.width,
          height: img.height,
          url: imageUrl
        });
        setLoading(false);
      };
      
      img.onerror = (error) => {
        console.error('Failed to load image:', error);
        console.log('Trying alternative loading method...');
        
        // Fallback: try loading via fetch to handle CORS
        fetch(imageUrl)
          .then(response => response.blob())
          .then(blob => {
            const objectUrl = URL.createObjectURL(blob);
            const fallbackImg = new Image();
            fallbackImg.onload = () => {
              console.log('Fallback image loaded:', fallbackImg.width, 'x', fallbackImg.height);
              setSelectedImageData({
                image: fallbackImg,
                width: fallbackImg.width,
                height: fallbackImg.height,
                url: objectUrl
              });
              setLoading(false);
            };
            fallbackImg.src = objectUrl;
          })
          .catch(fetchError => {
            console.error('Fallback loading also failed:', fetchError);
            setLoading(false);
          });
      };
      
      img.src = imageUrl;
    } catch (error) {
      console.error('Failed to load image data:', error);
      setLoading(false);
    }
  };

  const initializeRenderer = () => {
    if (!canvasRef.current) return;

    // Create Three.js scene
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
    
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ 
        canvas: canvasRef.current, 
        alpha: true,
        premultipliedAlpha: false,
        antialias: true,
        failIfMajorPerformanceCaveat: false
      });
      
      renderer.setSize(800, 600);
      renderer.setClearColor(0x000000, 0);
      
      // Add WebGL context loss/restore handlers
      canvasRef.current.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        console.warn('WebGL context lost, pausing rendering');
      });
      
      canvasRef.current.addEventListener('webglcontextrestored', () => {
        console.log('WebGL context restored, reinitializing');
        // The renderer should be recreated when context is restored
        if (rendererRef.current) {
          rendererRef.current.dispose();
        }
        // Trigger re-initialization
        setTimeout(() => {
          initializeOverlay();
        }, 100);
      });
      
    } catch (webglError) {
      console.warn('WebGL not supported, falling back to Canvas 2D:', webglError);
      // Fallback to Canvas 2D rendering
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'rgba(200, 200, 200, 0.1)';
      ctx.fillRect(0, 0, 800, 600);
      ctx.fillStyle = 'black';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('3D Overlay unavailable (WebGL not supported)', 400, 300);
      ctx.fillText('Basic image display active', 400, 320);
      return;
    }
    camera.position.z = 5;

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
  };

  const createPointCloudMaterial = () => {
    return new THREE.PointsMaterial({
      size: 0.02,
      vertexColors: true,
      transparent: true,
      opacity: overlayOpacity,
      sizeAttenuation: false
    });
  };

  const renderOverlay = () => {
    if (!canvasRef.current || !selectedImageData || !modelData) {
      console.log('Missing required data for overlay rendering');
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match container (responsive)
    const containerWidth = Math.min(800, selectedImageData.width);
    const containerHeight = Math.min(600, (selectedImageData.height * containerWidth) / selectedImageData.width);
    
    canvas.width = containerWidth;
    canvas.height = containerHeight;
    canvas.style.width = containerWidth + 'px';
    canvas.style.height = containerHeight + 'px';
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background image (scaled to fit canvas)
    ctx.drawImage(
      selectedImageData.image, 
      0, 0, selectedImageData.width, selectedImageData.height,
      0, 0, canvas.width, canvas.height
    );
    
    console.log('Drew background image:', canvas.width, 'x', canvas.height);
    
    if (overlayVisible && modelData.vertices) {
      // Create overlay for 3D model
      renderModelOverlay(ctx);
      console.log('Rendered 3D model overlay');
    }
  };

  // Helper function to get depth-based color
  const getDepthColor = (depth) => {
    // Normalize depth to 0-1 range (assuming depth is between 0 and max depth)
    const normalizedDepth = Math.abs(depth) / 0.8; // Max expected depth
    const clampedDepth = Math.min(1, Math.max(0, normalizedDepth));
    
    // Color mapping: Green (shallow) -> Yellow -> Orange -> Red (deep)
    if (clampedDepth < 0.25) {
      // Green to light green
      return { r: 50 + clampedDepth * 100, g: 200, b: 50 + clampedDepth * 50, depthLevel: 1 };
    } else if (clampedDepth < 0.5) {
      // Light green to yellow
      const t = (clampedDepth - 0.25) / 0.25;
      return { r: 150 + t * 100, g: 200, b: 100 - t * 100, depthLevel: 2 };
    } else if (clampedDepth < 0.75) {
      // Yellow to orange
      const t = (clampedDepth - 0.5) / 0.25;
      return { r: 250, g: 200 - t * 80, b: 50 - t * 50, depthLevel: 3 };
    } else {
      // Orange to red
      const t = (clampedDepth - 0.75) / 0.25;
      return { r: 250, g: 120 - t * 80, b: 30 - t * 30, depthLevel: 4 };
    }
  };

  const renderModelOverlay = (ctx) => {
    const vertices = modelData.vertices;
    const colors = modelData.colors;
    const bounds = modelData.bounds;
    
    if (!vertices || vertices.length === 0) return;

    console.log('Rendering professional medical overlay with', vertices.length / 3, 'points');

    // Use canvas dimensions for scaling
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    
    // Calculate transform parameters
    const centerX = canvasWidth / 2 + modelPosition.x;
    const centerY = canvasHeight / 2 + modelPosition.y;
    
    const baseScale = Math.min(canvasWidth, canvasHeight) * 0.5;
    const finalScale = baseScale * modelScale;
    
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    
    // Apply transforms
    ctx.translate(centerX, centerY);
    ctx.rotate(modelRotation * Math.PI / 180);

    // Step 1: Draw 3D Wireframe Grid (like in reference image)
    const ellipseWidth = finalScale * 0.6;
    const ellipseHeight = finalScale * 0.4;
    
    if (showWireframe) {
      const gridSize = 12; // Number of grid lines
      
      ctx.globalAlpha = overlayOpacity * 0.6;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      
      // Draw horizontal grid lines (curved to follow ellipse)
      for (let i = 0; i <= gridSize; i++) {
        const t = i / gridSize;
        const y = (t - 0.5) * ellipseHeight * 2;
        const xRadius = ellipseWidth * Math.sqrt(1 - Math.pow(y / ellipseHeight, 2));
        
        if (!isNaN(xRadius)) {
          ctx.beginPath();
          ctx.moveTo(-xRadius, y);
          ctx.lineTo(xRadius, y);
          ctx.stroke();
        }
      }
      
      // Draw vertical grid lines (curved to follow ellipse)  
      for (let i = 0; i <= gridSize; i++) {
        const t = i / gridSize;
        const x = (t - 0.5) * ellipseWidth * 2;
        const yRadius = ellipseHeight * Math.sqrt(1 - Math.pow(x / ellipseWidth, 2));
        
        if (!isNaN(yRadius)) {
          ctx.beginPath();
          ctx.moveTo(x, -yRadius);
          ctx.lineTo(x, yRadius);
          ctx.stroke();
        }
      }
      
      // Draw outer wireframe boundary
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.ellipse(0, 0, ellipseWidth, ellipseHeight, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Step 2: Draw Color-Coded Depth Points (regular grid pattern)
    if (showDepthPoints) {
      const pointsPerRow = 15;
      const pointsPerCol = 10;
      
      ctx.globalAlpha = overlayOpacity * 0.9;
      
      for (let row = 0; row < pointsPerCol; row++) {
        for (let col = 0; col < pointsPerRow; col++) {
          // Calculate position in ellipse
          const u = (col / (pointsPerRow - 1) - 0.5) * 2; // -1 to 1
          const v = (row / (pointsPerCol - 1) - 0.5) * 2; // -1 to 1
          
          // Check if point is inside ellipse
          if (u * u + v * v > 1) continue;
          
          const x = u * ellipseWidth;
          const y = v * ellipseHeight;
          
          // Calculate depth based on position (simulate burn crater)
          const distFromCenter = Math.sqrt(u * u + v * v);
          const depth = Math.pow(1 - distFromCenter, 1.5) * 0.8; // Deeper in center
          
          // Add some variation for realism
          const variation = (Math.sin(u * 8) + Math.cos(v * 6)) * 0.15;
          const finalDepth = Math.max(0, depth + variation);
          
          // Get color based on depth
          const colorData = getDepthColor(finalDepth);
          const { r, g, b } = colorData;
          
          // Draw depth point
          const pointSize = 8 + finalDepth * 4;
          
          // Create gradient for more professional look
          const gradient = ctx.createRadialGradient(x, y, 0, x, y, pointSize);
          gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.9)`);
          gradient.addColorStop(0.7, `rgba(${r * 0.8}, ${g * 0.8}, ${b * 0.8}, 0.7)`);
          gradient.addColorStop(1, `rgba(${r * 0.6}, ${g * 0.6}, ${b * 0.6}, 0.3)`);
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, pointSize, 0, Math.PI * 2);
          ctx.fill();
          
          // Add small white dot in center for precision
          ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    
    ctx.restore();
    
    // Step 3: Draw Color Scale and Depth Annotations (outside of transform)
    if (showColorScale) {
      drawColorScale(ctx, canvasWidth, canvasHeight);
    }
    
    // Step 4: Draw Medical Annotations (outside of transform)
    if (showAnnotations) {
      drawMedicalAnnotations(ctx, canvasWidth, canvasHeight);
    }
    
    console.log('Professional medical overlay rendered');
  };

  // Draw color scale (depth legend)
  const drawColorScale = (ctx, canvasWidth, canvasHeight) => {
    ctx.save();
    
    // Position scale on right side
    const scaleX = canvasWidth - 80;
    const scaleY = 50;
    const scaleHeight = 200;
    const scaleWidth = 20;
    
    // Draw scale background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(scaleX - 5, scaleY - 10, scaleWidth + 50, scaleHeight + 20);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 1;
    ctx.strokeRect(scaleX - 5, scaleY - 10, scaleWidth + 50, scaleHeight + 20);
    
    // Draw color gradient
    for (let i = 0; i < scaleHeight; i++) {
      const depth = i / scaleHeight;
      const colorData = getDepthColor(depth);
      const { r, g, b } = colorData;
      
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fillRect(scaleX, scaleY + scaleHeight - i, scaleWidth, 1);
    }
    
    // Draw scale border
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.lineWidth = 2;
    ctx.strokeRect(scaleX, scaleY, scaleWidth, scaleHeight);
    
    // Add scale labels
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.font = '14px Arial';
    ctx.textAlign = 'left';
    
    const labels = ['5', '4', '3', '2', '1'];
    for (let i = 0; i < labels.length; i++) {
      const y = scaleY + (i * scaleHeight / (labels.length - 1));
      ctx.fillText(labels[i], scaleX + scaleWidth + 5, y + 5);
      
      // Draw tick marks
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(scaleX + scaleWidth, y);
      ctx.lineTo(scaleX + scaleWidth + 3, y);
      ctx.stroke();
    }
    
    // Add scale title
    ctx.save();
    ctx.translate(scaleX - 20, scaleY + scaleHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Depth (mm)', 0, 0);
    ctx.restore();
    
    ctx.restore();
  };

  // Draw medical annotations
  const drawMedicalAnnotations = (ctx, canvasWidth, canvasHeight) => {
    ctx.save();
    
    // Analyze burn classification based on depth distribution
    const classification = analyzeBurnClassification();
    
    // Draw classification label at bottom
    const labelY = canvasHeight - 30;
    
    // Background for text
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(10, labelY - 20, canvasWidth - 20, 35);
    
    // Classification text
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`SKINOPATHY Analysis: ${classification}`, 20, labelY);
    
    // Add timestamp
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '12px Arial';
    ctx.textAlign = 'right';
    const timestamp = new Date().toLocaleString();
    ctx.fillText(`Generated: ${timestamp}`, canvasWidth - 20, labelY);
    
    ctx.restore();
  };

  // Analyze burn classification based on depth pattern
  const analyzeBurnClassification = () => {
    // This would analyze the depth distribution to determine burn type
    // For now, return a realistic classification based on the overlay
    const classifications = [
      'Deep partial-thickness (second-degree) burn',
      'Superficial partial-thickness burn', 
      'Mixed-depth partial-thickness burn',
      'Full-thickness (third-degree) burn'
    ];
    
    // Simple logic based on model data
    return classifications[1]; // Default to partial-thickness for demo
  };

  const handleModelPositionChange = (axis, value) => {
    setModelPosition(prev => ({
      ...prev,
      [axis]: value
    }));
  };

  const resetTransform = () => {
    setModelScale(1.0);
    setModelPosition({ x: 0, y: 0 });
    setModelRotation(0);
  };

  const downloadOverlay = () => {
    if (!canvasRef.current) {
      alert('No overlay to download');
      return;
    }
    
    try {
      // Ensure the canvas has content
      renderOverlay();
      
      // Create download link
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL('image/png', 1.0);
      
      const link = document.createElement('a');
      link.download = `burn_overlay_${selectedImage || 'image'}.png`;
      link.href = dataURL;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Downloaded overlay image successfully');
    } catch (error) {
      console.error('Failed to download overlay:', error);
      alert('Failed to download overlay image');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-full overflow-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-6 h-6 text-blue-600" />
                3D Model Image Overlay
              </div>
              <Button variant="outline" onClick={onClose}>
                ✕ Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Controls Panel */}
              <div className="space-y-6">
                {/* Image Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Image</label>
                  <Select value={selectedImage || ''} onValueChange={setSelectedImage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose image..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableImages.map((img) => (
                        <SelectItem key={img.filename} value={img.filename}>
                          {img.original_filename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Overlay Controls */}
                <div className="space-y-4">
                  <h4 className="font-medium">Overlay Controls</h4>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm">Visibility</label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setOverlayVisible(!overlayVisible)}
                      >
                        {overlayVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Opacity: {Math.round(overlayOpacity * 100)}%</label>
                    <Slider
                      value={[overlayOpacity]}
                      onValueChange={([value]) => setOverlayOpacity(value)}
                      min={0.1}
                      max={1.0}
                      step={0.05}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Scale: {modelScale.toFixed(2)}x</label>
                    <Slider
                      value={[modelScale]}
                      onValueChange={([value]) => setModelScale(value)}
                      min={0.1}
                      max={3.0}
                      step={0.1}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Rotation: {modelRotation}°</label>
                    <Slider
                      value={[modelRotation]}
                      onValueChange={([value]) => setModelRotation(value)}
                      min={-180}
                      max={180}
                      step={5}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Position X: {modelPosition.x}</label>
                    <Slider
                      value={[modelPosition.x]}
                      onValueChange={([value]) => handleModelPositionChange('x', value)}
                      min={-200}
                      max={200}
                      step={5}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Position Y: {modelPosition.y}</label>
                    <Slider
                      value={[modelPosition.y]}
                      onValueChange={([value]) => handleModelPositionChange('y', value)}
                      min={-200}
                      max={200}
                      step={5}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Visualization Controls */}
                <div className="space-y-4">
                  <h4 className="font-medium">Medical Visualization</h4>
                  
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={showWireframe}
                        onChange={(e) => setShowWireframe(e.target.checked)}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium">3D Wireframe Grid</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={showDepthPoints}
                        onChange={(e) => setShowDepthPoints(e.target.checked)}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium">Depth Color Points</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={showColorScale}
                        onChange={(e) => setShowColorScale(e.target.checked)}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium">Depth Scale Legend</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={showAnnotations}
                        onChange={(e) => setShowAnnotations(e.target.checked)}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium">Medical Annotations</span>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={resetTransform}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset Transform
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={downloadOverlay}
                    className="w-full"
                    disabled={!selectedImageData || !overlayVisible}
                    data-testid="download-overlay-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Medical Overlay
                  </Button>
                </div>

                {/* Info Panel */}
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <h5 className="font-medium text-blue-800 mb-2">Instructions</h5>
                  <ul className="text-blue-700 space-y-1 text-xs">
                    <li>• Select an image to overlay the 3D model</li>
                    <li>• Adjust opacity to see through the model</li>
                    <li>• Scale and position the model to align with the burn</li>
                    <li>• Rotate to match the image perspective</li>
                    <li>• Download the final overlay image</li>
                  </ul>
                </div>
              </div>

              {/* Canvas Display */}
              <div className="lg:col-span-3">
                <div className="border rounded-lg overflow-hidden bg-gray-100">
                  {loading ? (
                    <div className="h-96 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-gray-600">Loading image...</p>
                      </div>
                    </div>
                  ) : selectedImageData ? (
                    <div className="relative">
                      <canvas
                        ref={canvasRef}
                        className="max-w-full h-auto"
                        style={{ 
                          maxHeight: '600px',
                          objectFit: 'contain'
                        }}
                      />
                      <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                        {selectedImage} ({selectedImageData.width}×{selectedImageData.height})
                      </div>
                      {overlayVisible && (
                        <div className="absolute top-2 right-2 bg-blue-500 bg-opacity-75 text-white px-2 py-1 rounded text-xs">
                          3D Overlay Active
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-96 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Select an image to begin overlay</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImageOverlay;